import * as React from "react"
import { AnyDataPlan, ReportData, ReportOptions, ReportType } from ".."
import { dataPlanToReportData, isSharedDataPlan, defaultChartSettings } from "../reports-helpers"
import ReportDetails, { reportDetailsFromData } from "../components/ReportDetails"
import ReportPageTemplate from "./ReportPageTemplate"
import ColumnChart, { ColumnChartProps } from "../../components/organisms/charts/ColumnChart"
import { SvgAsImage } from "../hooks/useRenderSvgToImg"
import {
  RESOLUTIONS,
  usageDataToColumnAllTimeShared,
  getAllTimeColumnLabels,
  usageDataToColumnDataYearShared,
  usageDataToColumnDataMonthShared,
  usageDataToColumnDataDayShared,
} from "../../utils/chart-helpers"
import { yearLabelsColumn, monthLabelsColumn, dayLabelsColumn } from "../../utils/data"
import { ChartSettings } from "../../components/organisms/charts/SharedDataChartView"

/**
 * Returns props for a ColumnChart component from a dataplan.
 */
export const dataPlanChartProps = (
  { fromTimestamp, toTimestamp, resolution }: ReportType<ReportOptions, "dataplan">,
  { dataPlan, sharedPlan }: ReportType<ReportData, "dataplan">,
): ColumnChartProps => {
  const result = {
    animate: false,
    columnWidth: 16,
  }

  const usageMetrics = (sharedPlan ? dataPlan.totalUsageMetrics?.allUsages : dataPlan.totalUsageMetrics) ?? []

  switch (resolution) {
    case RESOLUTIONS.allTime:
      return {
        ...result,
        data: usageDataToColumnAllTimeShared(usageMetrics, fromTimestamp, toTimestamp),
        labels: getAllTimeColumnLabels(fromTimestamp, toTimestamp),
      }
    case RESOLUTIONS.year:
      return {
        ...result,
        data: usageDataToColumnDataYearShared(usageMetrics),
        labels: yearLabelsColumn,
      }
    case RESOLUTIONS.month:
      return {
        ...result,
        data: usageDataToColumnDataMonthShared(usageMetrics, fromTimestamp),
        labels: monthLabelsColumn,
      }
    case RESOLUTIONS.day:
      return {
        ...result,
        data: usageDataToColumnDataDayShared(usageMetrics),
        labels: dayLabelsColumn,
      }
  }
}

const ReportDataPlanChart = ({
  data,
  options,
}: {
  data: ReportType<ReportData, "dataplan">
  options: ReportType<ReportOptions, "dataplan">
}) => {
  return (
    <SvgAsImage alt="Dataplan usage chart" scale={2} style={{ width: "100%" }}>
      <ColumnChart {...dataPlanChartProps(options, data)} />
    </SvgAsImage>
  )
}

const ReportDataPlanPage = ({ dataPlan, chartSettings }: { dataPlan: AnyDataPlan; chartSettings: ChartSettings }) => {
  const reportOptions: ReportType<ReportOptions, "dataplan"> & ChartSettings = {
    ...chartSettings,
    type: "dataplan",
    apiPlanId: dataPlan.apiPlanId,
    sharedPlan: isSharedDataPlan(dataPlan),
  }
  return (
    <ReportPageTemplate subtitle={dataPlan.name || `Dataplan #${dataPlan.apiPlanId}`}>
      <ReportDetails details={reportDetailsFromData(dataPlanToReportData(dataPlan))} />
      {dataPlan.totalUsageMetrics && (
        <ReportDataPlanChart data={dataPlanToReportData(dataPlan)} options={reportOptions} />
      )}
    </ReportPageTemplate>
  )
}

export default ReportDataPlanPage
